import React from "react"
import BigCalendar from "react-big-calendar"
import moment from "moment"
import autobind from "class-autobind"
import LeaveTrackingCalendarViewPopup from "./LeaveTrackingCalendarViewPopup"
import { MonthEvent, ILeaveRequestEvent } from "."
import { ITimeoffRequestDocument, ETimeoffStatus, IHolidayInstanceForPunch, ELoggedInUserType } from "../../../models"
import * as timeHelper from "../../../helpers/timeHelper"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "./styles.css"
import AppStateStore from "../../../stores/AppStateStore"
import strings from "../../../localization/strings"
import * as leaveTrackingActions from "../../../actions/LeaveTrackingActions"
import LeaveTrackingNotesDrawer from "../LeaveTrackingNotesDrawer"
import * as timeoffHelper from "../../../helpers/timeoffHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import * as permissionHelper from "../../../helpers/permissionHelper"

interface IProps {
  pageName: "UserDetailLeaveTracking" | "LeaveTracking"
  loggedInUserType: ELoggedInUserType
  leaveRequests: ITimeoffRequestDocument[]
  refereceDate: Date
  holidays?: IHolidayInstanceForPunch[]
  handleUpdate(leaveRequest: ITimeoffRequestDocument): void
  handleDelete(leaveRequest: ITimeoffRequestDocument): void
  updateStatusCb(): void
}
interface IState {
  showPopup: boolean
  editPermission: boolean
  deletePermission: boolean
  updateStatusPermission: boolean
  popupX: number
  popupY: number
  popupPayload: ITimeoffRequestDocument
  leaveRequestForDrawerId: string
}

export class LeaveTrackingCalendarView extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)
    this.state = {
      showPopup: false,
      editPermission: false,
      deletePermission: false,
      updateStatusPermission: false,
      popupX: null,
      popupY: null,
      popupPayload: null,
      leaveRequestForDrawerId: null
    }
    autobind(this)
  }

  componentDidMount() {
    document.addEventListener("click", this.handleClickDocument)
    if (this.props.pageName === "UserDetailLeaveTracking") {
      const targetEl = document.getElementsByClassName("LeaveCalendar-container")[0] as HTMLElement
      targetEl.style.height = "375px"
    }
  }

  componentWillUnmount() {
    document.removeEventListener("click", this.handleClickDocument)
  }

  handleClickDocument() {
    this.setState({ showPopup: false, popupX: null, popupY: null, popupPayload: null })
  }

  handlePopup(leaveRequest: ITimeoffRequestDocument, e: React.MouseEvent<HTMLElement>) {
    const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
    const windowHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);

    const editPermission =
      this.props.loggedInUserType === ELoggedInUserType.Employee ||
      (leaveRequest.UserId === AuthenticationStore.getLoggedInUser().Id ?
        true :
        permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.EditLeaveTracking)
      )

    const deletePermission =
      this.props.loggedInUserType === ELoggedInUserType.Employee ||
      (leaveRequest.UserId === AuthenticationStore.getLoggedInUser().Id ?
        true :
        permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.DeleteLeaveTracking)
      )

    const updateStatusPermission =
      this.props.loggedInUserType !== ELoggedInUserType.Employee &&
      (leaveRequest.UserId === AuthenticationStore.getLoggedInUser().Id ?
        permissionHelper.hasPermission(AuthenticationStore.Permissions.LeaveTracking.AllowSupervisorToApproveDenyOwnLeaveRequests) :
        true
      )

    let popupMenuItemCount = 1;
    if (editPermission && leaveRequest.Status === ETimeoffStatus.Requested) {
      popupMenuItemCount += 1
    }
    if (updateStatusPermission && leaveRequest.Status === ETimeoffStatus.Requested) {
      popupMenuItemCount += 2
    }
    if (deletePermission) {
      popupMenuItemCount += 1
    }

    const popupHeight = popupMenuItemCount * 40
    const popupWidth = 110
    this.setState({
      showPopup: true,
      editPermission,
      deletePermission,
      updateStatusPermission,
      popupX: e.clientX + popupWidth < windowWidth ? e.clientX : e.clientX - popupWidth,
      popupY: e.clientY + popupHeight < windowHeight ? e.clientY : e.clientY - popupHeight,
      popupPayload: leaveRequest
    })
  }

  render() {
    const monthStartDate = moment(this.props.refereceDate).startOf("month").format()
    const monthEndDate = moment(this.props.refereceDate).endOf("month").format()

    const tempEvents: ILeaveRequestEvent[] = []
    this.props.leaveRequests.forEach(item => {
      const timeoffEndDate = timeHelper.keepConvertUTCToLocal(item.EndDate)
      let date = timeHelper.keepConvertUTCToLocal(item.StartDate)
      while (moment(date).isBefore(timeoffEndDate)) {
        if (moment(date).isSameOrAfter(monthStartDate) && moment(date).isBefore(monthEndDate)) {
          timeoffHelper.checkDateTimeLocalValid(item, date) &&
            tempEvents.push({
              start: moment(date).toDate(),
              end: moment(date).toDate(),
              leaveRequest: item,
              handleUpdate: leaveRequest => this.props.handleUpdate(leaveRequest),
              onRightClick: this.handlePopup
            })
        }
        date = moment(date).add(1, "day").format()
      }
    })

    const events: ILeaveRequestEvent[] = []
    let date1 = monthStartDate
    while (moment(date1).isBefore(monthEndDate)) {
      if (this.props.holidays) {
        const holiday = this.props.holidays.find(item => moment(item.Holiday).isSame(moment(date1).utc(true)))
        holiday &&
          events.push({
            start: moment(timeHelper.keepConvertUTCToLocal(holiday.Holiday)).toDate(),
            end: moment(timeHelper.keepConvertUTCToLocal(holiday.Holiday)).toDate(),
            holidayName: holiday.HolidayName
          })
      }
      const dayEvent = tempEvents.filter(item => moment(item.start).isSame(date1))
      dayEvent.forEach(item => events.push(item))
      date1 = moment(date1).add(1, "day").format()
    }

    events.sort((a, b) => {
      if (!a.leaveRequest || !b.leaveRequest) {
        return a.holidayName ? -1 : 0
      }
      else {
        if (a.leaveRequest.Status === b.leaveRequest.Status) {
          return 0
        }
        else if (a.leaveRequest.Status < b.leaveRequest.Status) {
          return -1
        }
        else if (a.leaveRequest.Status > b.leaveRequest.Status) {
          return 1
        }
      }
    })

    return (
      <div>
        <BigCalendar
          popup
          views={{ month: true, week: false }}
          defaultView={BigCalendar.Views.MONTH}
          step={60}
          className={"LeaveCalendar-container"}
          localizer={BigCalendar.momentLocalizer(moment)}
          events={events}
          defaultDate={this.props.refereceDate}
          eventPropGetter={() => ({ className: "LeaveCalendar-Event-Node" })}
          toolbar={false}
          components={{
            month: { event: MonthEvent }
          }}
        />
        {this.state.showPopup &&
          <LeaveTrackingCalendarViewPopup
            popupX={this.state.popupX}
            popupY={this.state.popupY}
            payload={this.state.popupPayload}
            editPermission={this.state.editPermission}
            updateStatusPermisson={this.state.updateStatusPermission}
            deletePermission={this.state.deletePermission}
            handleUpdate={leaveRequest => {
              this.setState({ showPopup: false, popupX: null, popupY: null, popupPayload: null })
              this.props.handleUpdate(leaveRequest)
            }}
            handleDelete={leaveRequest => {
              this.setState({ showPopup: false, popupX: null, popupY: null, popupPayload: null })
              this.props.handleDelete(leaveRequest)
            }}
            handleUpdateLeaveRequestStatus={(status, leaveRequest) => {
              this.setState({ showPopup: false, popupX: null, popupY: null, popupPayload: null })

              AppStateStore.setConfirmDialogContent({
                content: strings.formatString(
                  status === ETimeoffStatus.Approved ? strings.Are_you_sure_you_want_to_approve_leave_request_of_employee : strings.Are_you_sure_you_want_to_deny_leave_request_of_employee,
                  leaveRequest.EmployeeName,
                  moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY") === moment(timeHelper.keepConvertUTCToLocal(leaveRequest.EndDate)).format("DD/MM/YYYY") ?
                    moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY") :
                    `${moment(timeHelper.keepConvertUTCToLocal(leaveRequest.StartDate)).format("DD/MM/YYYY")} - ${moment(timeHelper.keepConvertUTCToLocal(leaveRequest.EndDate)).format("DD/MM/YYYY")}`
                ) as string,
                confirm: () => leaveTrackingActions.handleUpdateStatus(status, [leaveRequest], this.props.updateStatusCb)
              })
            }}
            handleNotesDrawer={leaveRequestForDrawerId => {
              this.setState({ showPopup: false, popupX: null, popupY: null, popupPayload: null, leaveRequestForDrawerId })
            }}
          />
        }
        <LeaveTrackingNotesDrawer
          open={this.state.leaveRequestForDrawerId ? true : false}
          leaveRequestId={this.state.leaveRequestForDrawerId}
          handleClose={() => this.setState({ leaveRequestForDrawerId: null })}
        />
      </div>
    )
  }
}
